import { useCallback } from "react";
import { useSearchParams } from "react-router-dom";

import { useOrganizationSelection } from "../../../hooks/useOrganizationSelection";

import { CATALOGUE_REGISTRY, DEFAULT_CATALOGUE_ID } from "./catalogueRegistry";
import { isCatalogueId } from "./marketingConfigurationHelpers";
import { CatalogueId, FeedbackQuestionItem, ViewingTimeSlotItem } from "./types";
import { useMarketingConfigurationCatalogue } from "./useMarketingConfigurationCatalogue";
import { useMarketingConfigurationEditor } from "./useMarketingConfigurationEditor";

export function useMarketingConfigurationModule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const { orgId, organizations, organizationOptions, changeSelectedOrgId, isLoadingOrganizations } =
    useOrganizationSelection();

  const categoryParam = searchParams.get("category");
  const catalogueId: CatalogueId = isCatalogueId(categoryParam) ? categoryParam : DEFAULT_CATALOGUE_ID;
  const catalogue = CATALOGUE_REGISTRY.find((item) => item.id === catalogueId) ?? CATALOGUE_REGISTRY[0];

  const changeCategory = useCallback(
    (nextCatalogueId: CatalogueId) => {
      const updatedParams = new URLSearchParams(searchParams);
      updatedParams.set("category", nextCatalogueId);
      setSearchParams(updatedParams);
    },
    [searchParams, setSearchParams]
  );

  const {
    items,
    marketingPublicListUrl,
    isLoading: isLoadingCatalogue,
    refetch,
  } = useMarketingConfigurationCatalogue(orgId, catalogueId);

  const existingTimeSlots = catalogueId === "viewingTimeSlotsConfig" ? (items as ViewingTimeSlotItem[]) : [];
  const existingFeedbackQuestions =
    catalogueId === "feedbackQuestionnaireConfig" ? (items as FeedbackQuestionItem[]) : [];

  const editor = useMarketingConfigurationEditor({
    orgId,
    organizationOptions,
    catalogueId,
    items,
    refetch,
  });

  return {
    orgId,
    organizations,
    organizationOptions,
    changeSelectedOrgId,
    catalogueId,
    catalogue,
    catalogues: CATALOGUE_REGISTRY,
    changeCategory,
    items,
    marketingPublicListUrl,
    existingTimeSlots,
    existingFeedbackQuestions,
    isLoading: isLoadingOrganizations || isLoadingCatalogue || editor.isSaving,
    ...editor,
  };
}
